import React from 'react'
import { t } from '../i18n'
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  linkPlugin,
  linkDialogPlugin,
  tablePlugin,
  thematicBreakPlugin,
  searchPlugin,
  diffSourcePlugin,
  toolbarPlugin,
  DiffSourceToggleWrapper,
  UndoRedo,
  Separator,
  BoldItalicUnderlineToggles,
  StrikeThroughSupSubToggles,
  CodeToggle,
  HighlightToggle,
  BlockTypeSelect,
  ListsToggle,
  CreateLink,
  InsertTable,
  InsertThematicBreak,
} from '@mdxeditor/editor'
import plainTextToMarkdown from './plainTextToMarkdown'
import './TextEditor.css?inject'
import '@mdxeditor/editor/style.css'


export const FORMAT_MARKDOWN = 'markdown'
export const FORMAT_PLAIN = 'plain'

export type TextEditorFormat = typeof FORMAT_MARKDOWN | typeof FORMAT_PLAIN

interface TextEditorProps {
  initialValue: string
  onChange: (markdown: string) => void
  // Raw, unresolved format from the caller; missing/unrecognized values
  // default to plain once resolved below (see `format` in the component body).
  format?: string | null
}

const TextEditor: React.FC<TextEditorProps> = ({
  initialValue,
  onChange,
  format: rawFormat,
}) => {
  const format: TextEditorFormat =
    rawFormat === FORMAT_MARKDOWN ? FORMAT_MARKDOWN : FORMAT_PLAIN

  // markdown values are passed to the editor as-is: everything written
  // through the editor is escaped by its serializer on save
  const value = initialValue || ''
  const markdown = format === FORMAT_PLAIN ? plainTextToMarkdown(value) : value

  // a legacy plain-text description is saved in its converted Markdown
  // form even when the user never edits the field, so the conversion is
  // reported as the field value once, as soon as the editor mounts —
  // but only when escaping actually changed something, so an untouched
  // value that needed no conversion (e.g. empty, or plain text with no
  // Markdown-special characters) doesn't signal a change for no reason
  React.useEffect(() => {
    if (format === FORMAT_PLAIN && markdown !== value) {
      onChange(markdown)
    }
  }, [])

  return (
    <MDXEditor
      className='mdxeditor'
      markdown={markdown}
      // the editor normalizes the loaded value on mount and reports it
      // through onChange; forwarding that event would replace the field
      // value with the editor's own serialization before any user edit —
      // until then the value to save is the stored one (markdown mode) or
      // the conversion reported above (plain mode)
      onChange={(value, initialMarkdownNormalize) => {
        if (!initialMarkdownNormalize) {
          onChange(value)
        }
      }}
      translation={t}
      plugins={[
        headingsPlugin(),
        listsPlugin(),
        quotePlugin(),
        linkPlugin(),
        linkDialogPlugin(),
        tablePlugin(),
        thematicBreakPlugin(),
        searchPlugin(),
        diffSourcePlugin({
          // diff against the value as loaded into the editor, so an
          // untouched document shows an empty diff
          diffMarkdown: markdown
        }),
        toolbarPlugin({
          toolbarContents: () => (
            <DiffSourceToggleWrapper>
              <UndoRedo />
              <Separator />
              <BoldItalicUnderlineToggles />
              <StrikeThroughSupSubToggles options={['Strikethrough']} />
              <CodeToggle />
              <HighlightToggle />
              <Separator />
              <BlockTypeSelect />
              <Separator />
              <ListsToggle />
              <Separator />
              <CreateLink />
              <InsertTable />
              <InsertThematicBreak />
            </DiffSourceToggleWrapper>
          ),
        }),
      ]}
    />
  )
}

export default TextEditor
